import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PifSidebar } from '@/components/PifSidebar';
import { CompanyGrid } from '@/components/CompanyGrid';
import { useCategories, useFilteredSheets } from '@/hooks/useCategories';
import { useAccounts } from '@/hooks/useAccounts';
import { useSheetSelection } from '@/contexts/sheetSelection';
import http from '@/services/http';
import type { CompanySheet } from '@/types/category';

export function BrowseView() {
  const { accountId: accountIdParam } = useParams<{ accountId: string }>();
  const { accounts } = useAccounts();
  const navigate = useNavigate();
  const { setSelectedSheetId, setSelectedCategoryIds: setContextCategoryIds } = useSheetSelection();

  useEffect(() => {
    setSelectedSheetId(null);
    setContextCategoryIds([]);
  }, [setSelectedSheetId, setContextCategoryIds]);

  const rawId = accountIdParam ?? accounts[0]?.id;
  const accountId: number | null = rawId ? parseInt(rawId, 10) : null;

  const { categories, loading: catLoading } = useCategories(accountId);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(new Set());
  const [sectorMap, setSectorMap] = useState<Map<number, string>>(new Map());

  const categoryIdsArray = useMemo(() => Array.from(selectedCategoryIds), [selectedCategoryIds]);
  const { sheets: companies, loading: sheetsLoading } = useFilteredSheets(
    accountId,
    categoryIdsArray
  );

  const [allSheets, setAllSheets] = useState<CompanySheet[]>([]);
  const [allSheetsLoading, setAllSheetsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const showAll = selectedCategoryIds.size === 0;

  useEffect(() => {
    if (!showAll || !accountId) return;
    setAllSheetsLoading(true);
    http
      .get(`/v1/accounts/${accountId}/sheets`)
      .then((res) => {
        setAllSheets(
          res.data.map(
            (s: {
              id: number;
              title: string;
              sheet_color?: string;
              logo_url?: string | null;
              display_name?: string | null;
              description?: string | null;
            }) => ({
              id: s.id,
              title: s.title,
              display_name: s.display_name ?? null,
              logo_url: s.logo_url ?? null,
              description: s.description ?? null,
              sheet_color: s.sheet_color ?? null,
            })
          )
        );
      })
      .catch(console.error)
      .finally(() => setAllSheetsLoading(false));
  }, [showAll, accountId]);

  useEffect(() => {
    if (!accountId || !categories) {
      setSectorMap(new Map());
      return;
    }

    const sectorGroup = categories.groups.find((g) => g.group === "sector");
    const sectorCategories = sectorGroup?.categories ?? [];
    if (sectorCategories.length === 0) {
      setSectorMap(new Map());
      return;
    }

    let cancelled = false;
    const loadSectorMap = async () => {
      try {
        const responses = await Promise.all(
          sectorCategories.map((category) =>
            http
              .get(`/v1/accounts/${accountId}/categories/${category.id}/sheets`)
              .then((res) => ({ name: category.name, sheets: res.data as CompanySheet[] }))
          )
        );

        if (cancelled) return;
        const next = new Map<number, string>();
        for (const response of responses) {
          for (const sheet of response.sheets) {
            if (!next.has(sheet.id)) {
              next.set(sheet.id, response.name);
            }
          }
        }
        setSectorMap(next);
      } catch (error) {
        if (import.meta.env?.DEV) {
          console.error("Failed to build sector map", error);
        }
      }
    };

    void loadSectorMap();
    return () => {
      cancelled = true;
    };
  }, [accountId, categories]);

  const displayCompanies = useMemo(() => {
    const base = showAll ? allSheets : companies;
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((c) => {
      const name = (c.display_name || c.title || '').toLowerCase();
      const desc = (c.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [showAll, allSheets, companies, search]);
  const loading = catLoading || (showAll ? allSheetsLoading : sheetsLoading);

  const handleToggleCategory = useCallback((id: number) => {
    setSelectedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    setSelectedCategoryIds(new Set());
    setSearch('');
  }, []);

  const handleCompanyClick = useCallback(
    (id: number) => {
      const base = accountId ? `/account/${accountId}` : '';
      navigate(`${base}/chat/new?sheetId=${id}`);
    },
    [accountId, navigate]
  );

  const handleChatAboutCategories = useCallback(() => {
    if (selectedCategoryIds.size === 0) return;
    const ids = Array.from(selectedCategoryIds).join(',');
    const base = accountId ? `/account/${accountId}` : '';
    navigate(`${base}/chat/new?categoryIds=${ids}`);
  }, [selectedCategoryIds, accountId, navigate]);

  return (
    <div className="flex h-screen bg-peach p-6 gap-6">
      {/* Sidebar */}
      <div className="w-[300px] shrink-0">
        <PifSidebar
          categories={categories}
          selectedCategoryIds={selectedCategoryIds}
          onToggleCategory={handleToggleCategory}
          onResetFilters={handleResetFilters}
          loading={catLoading}
          search={search}
          onSearchChange={setSearch}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col gap-6 min-w-0">
        {/* Header bar */}
        <div className="bg-white border border-peach-1 rounded-lg h-[84px] flex items-center justify-between px-5 shrink-0">
          <h1 className="text-lg font-bold text-grey-black tracking-[0.3px]">
            {selectedCategoryIds.size > 0 ? 'Filtered companies' : 'All modeles'}
          </h1>
          <button
            onClick={handleChatAboutCategories}
            disabled={selectedCategoryIds.size === 0}
            className={`
              flex items-center gap-2 h-11 px-4 rounded-md text-md text-white transition-colors
              ${
                selectedCategoryIds.size > 0
                  ? 'bg-pif-green hover:bg-pif-green-light'
                  : 'bg-grey-medium cursor-not-allowed'
              }
            `}
          >
            <span>Chat about selected companies</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="shrink-0"
            >
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64 text-grey-hover text-lg">
              Loading...
            </div>
          ) : (
            <CompanyGrid
              companies={displayCompanies}
              onCompanyClick={handleCompanyClick}
              sectorMap={sectorMap}
            />
          )}
        </div>
      </div>
    </div>
  );
}
